/**
 * Générateur — Inventer des problèmes mettant en jeu des fractions
 * Attendu ID : 633 | Niveau : 6ème | Domaine : NC (Nombres et calculs)
 *
 * Ce générateur contient 1 EXERCICE DIDACTIQUEMENT DISTINCT décliné en
 * 3 formats UI (2 directions QCM + 1 relier) pour un total de 3 entrées
 * registry.
 *
 * Exercice couvert (voir FICHE_PEDAGO_CLUSTER18 §6.1 à §6.3) :
 *
 *   1. `associer_calcul_probleme`
 *       — QCM calcul → problème : « Quel problème ce calcul résout-il ? »
 *       — QCM problème → calcul : « Quel calcul traduit ce problème ? »
 *       — Relier : 4 paires calcul ↔ problème
 *
 * Distracteurs (refonte bilan prof D6, 2026-07-27) : voir REGLES_DISTRACTEURS_QCM
 *   en bas de fichier — tout distracteur porte sur LES données du problème
 *   posé et matérialise une erreur d'élève nommée (codes en _meta.params).
 */

import {
    randomChoice, shuffle,
    makeQcm, makeRelier,
} from '../../../utils';
import { gcd, simplify, fmtFrac, fmtKatex, fmtItem } from './fraction';

// ═══════════════════════════════════════════════════════════
// CONSTANTES & LABELS
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 633;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_CALC_PROB  = 'Associer un calcul à un problème (calcul → problème)';
const L_PROB_CALC  = 'Associer un problème à un calcul (problème → calcul)';
const L_RELIER     = 'Relier chaque calcul au problème correspondant';

const PRENOMS = [
    'Adam', 'Amélie', 'Chloé', 'Hugo', 'Inès', 'Jade',
    'Karim', 'Léo', 'Lucas', 'Manon', 'Sofia', 'Thomas', 'Yanis',
];

// ═══════════════════════════════════════════════════════════
// POOL — Couples (expression, énoncé, type)
// ═══════════════════════════════════════════════════════════

/**
 * Chaque entrée du pool est construite à la volée pour garantir la variété.
 * On a trois familles : somme, reste, multiplication.
 */

// Fraction pairs for additive problems [a1, b1, a2, b2], sum < 1
const FRAC_PAIRS = [
    [1, 4, 3, 8], [2, 5, 1, 10], [1, 3, 2, 9], [1, 6, 1, 3],
    [1, 4, 1, 6], [2, 3, 1, 6], [1, 3, 1, 4], [3, 8, 1, 4],
    [1, 5, 3, 10], [5, 12, 1, 4], [1, 2, 1, 6],
];

// Multiplication entries: [n, a, b] for n × a/b (périmètre)
const MULT_ENTRIES = [
    [3, 5, 7], [4, 11, 6], [3, 8, 5], [4, 7, 9], [3, 13, 4],
    [3, 7, 11], [4, 5, 8], [3, 11, 9], [4, 3, 7], [3, 10, 11],
];

// Context templates
const CTX_SOMME = [
    (n1, f1, n2, f2) => `${n1} mange ${f1} d'un gâteau et ${n2} en mange ${f2}. Quelle part ont-ils mangée au total\u00a0?`,
    (n1, f1, n2, f2) => `${n1} lit ${f1} d'un livre et ${n2} en lit ${f2}. Quelle part du livre ont-ils lue\u00a0?`,
    (n1, f1, n2, f2) => `${n1} cultive ${f1} d'un terrain et ${n2} en cultive ${f2}. Quelle part est cultivée\u00a0?`,
    (n1, f1, n2, f2) => `${n1} boit ${f1} d'une bouteille et ${n2} en boit ${f2}. Quelle part ont-ils bue\u00a0?`,
];

const CTX_RESTE = [
    (n1, f1, n2, f2) => `${n1} mange ${f1} d'un gâteau et ${n2} en mange ${f2}. Quelle part reste-t-il\u00a0?`,
    (n1, f1, n2, f2) => `${n1} prend ${f1} d'une pizza et ${n2} en prend ${f2}. Quelle part reste-t-il\u00a0?`,
    (n1, f1, n2, f2) => `${n1} dépense ${f1} de son argent et ${n2} en dépense ${f2}. Quelle part reste-t-il\u00a0?`,
];

const CTX_MULT = [
    (n, f) => `Un triangle équilatéral a un côté de ${f} cm. Quel est son périmètre\u00a0?`,
    (n, f) => `Un carré a un côté de ${f} cm. Quel est son périmètre\u00a0?`,
];

/**
 * Builds a random pool entry of the given type.
 * Returns { exprKatex, exprPlain, enonce, type }.
 */
const buildEntry = (type) => {
    const [n1, n2] = shuffle(PRENOMS).slice(0, 2);

    if (type === 'somme') {
        const [a1, b1, a2, b2] = randomChoice(FRAC_PAIRS);
        const ctx = randomChoice(CTX_SOMME);
        return {
            exprKatex: `${fmtKatex(a1, b1)} + ${fmtKatex(a2, b2)}`,
            exprPlain: `${fmtFrac(a1, b1)} + ${fmtFrac(a2, b2)}`,
            exprItem: `$${fmtKatex(a1, b1)} + ${fmtKatex(a2, b2)}$`,
            enonce: ctx(n1, fmtItem(a1, b1), n2, fmtItem(a2, b2)),
            type: 'somme',
            a1, b1, a2, b2,
        };
    } else if (type === 'reste') {
        const [a1, b1, a2, b2] = randomChoice(FRAC_PAIRS);
        const ctx = randomChoice(CTX_RESTE);
        return {
            exprKatex: `1 - ${fmtKatex(a1, b1)} - ${fmtKatex(a2, b2)}`,
            exprPlain: `1 - ${fmtFrac(a1, b1)} - ${fmtFrac(a2, b2)}`,
            exprItem: `$1 - ${fmtKatex(a1, b1)} - ${fmtKatex(a2, b2)}$`,
            enonce: ctx(n1, fmtItem(a1, b1), n2, fmtItem(a2, b2)),
            type: 'reste',
            a1, b1, a2, b2,
        };
    } else {
        // multiplication
        const [mult, a, b] = randomChoice(MULT_ENTRIES);
        const frac = fmtFrac(a, b);
        const forme = mult === 3 ? 'triangle équilatéral' : 'carré';
        const ctx = mult === 3 ? CTX_MULT[0] : CTX_MULT[1];
        return {
            exprKatex: `${mult} \\times ${fmtKatex(a, b)}`,
            exprPlain: `${mult} × ${frac}`,
            exprItem: `$${mult} \\times ${fmtKatex(a, b)}$`,
            enonce: ctx(mult, fmtItem(a, b)),
            type: 'multiplication',
            n: mult, a, b,
        };
    }
};

// ═══════════════════════════════════════════════════════════
// GRILLES DE DISTRACTEURS (refonte bilan prof D6, 2026-07-27)
// Principe : tout distracteur est construit sur LES DONNÉES du problème posé
// et matérialise une erreur d'élève NOMMÉE. (L'ancien buildDistractor tirait
// des entrées aléatoires : un seul choix contenait les nombres de l'énoncé →
// résoluble par appariement de surface, zéro valeur diagnostique.)
// ═══════════════════════════════════════════════════════════

/** Valeur numérique du calcul correct d'une entrée (garde d'unicité). */
const valeurEntry = (e) => {
    if (e.type === 'multiplication') return (e.n * e.a) / e.b;
    const v1 = e.a1 / e.b1, v2 = e.a2 / e.b2;
    return e.type === 'somme' ? v1 + v2 : 1 - v1 - v2;
};

/** Distracteurs-CALCULS d'une entrée : mêmes données, erreurs nommées. */
const distracteursPourCalcul = (e) => {
    if (e.type === 'multiplication') {
        const fk = fmtKatex(e.a, e.b);
        const v = e.a / e.b;
        return e.n === 4
            ? [
                { item: `$${fk} \\times ${fk}$`, v: v * v, code: 'aire_confusion' },
                { item: `$2 \\times ${fk}$`, v: 2 * v, code: 'double_cotes' },
                { item: `$4 + ${fk}$`, v: 4 + v, code: 'addition_confusion' },
            ]
            : [
                { item: `$4 \\times ${fk}$`, v: 4 * v, code: 'formule_carre' },
                { item: `$2 \\times ${fk}$`, v: 2 * v, code: 'double_cotes' },
                { item: `$3 + ${fk}$`, v: 3 + v, code: 'addition_confusion' },
            ];
    }
    const f1 = fmtKatex(e.a1, e.b1), f2 = fmtKatex(e.a2, e.b2);
    const v1 = e.a1 / e.b1, v2 = e.a2 / e.b2;
    // Soustraction : la plus grande écrite en premier (résultat positif)
    const [gK, pK, gV, pV] = v1 >= v2 ? [f1, f2, v1, v2] : [f2, f1, v2, v1];
    if (e.type === 'somme') {
        return [
            { item: `$1 - ${f1} - ${f2}$`, v: 1 - v1 - v2, code: 'complement' },
            { item: `$${f1} \\times ${f2}$`, v: v1 * v2, code: 'mult_confusion' },
            { item: `$${gK} - ${pK}$`, v: gV - pV, code: 'soustraction' },
        ];
    }
    return [
        { item: `$${f1} + ${f2}$`, v: v1 + v2, code: 'part_prise' },
        { item: `$1 - ${f1} + ${f2}$`, v: 1 - v1 + v2, code: 'erreur_signe' },
        { item: `$${gK} - ${pK}$`, v: gV - pV, code: 'soustraction' },
    ];
};

/** Problèmes-distracteurs d'une entrée : MÊMES nombres, autre question.
 * Garde d'exclusivité STRUCTURELLE, vérifiée gabarit par gabarit : aucun de
 * ces problèmes n'est résoluble par le calcul affiché (piège documenté :
 * « 4 amis mangent chacun a/b » se résout AUSSI par 4 × a/b — interdit). */
const problemesDistracteurs = (e) => {
    const [n1, n2] = shuffle(PRENOMS).slice(0, 2);
    if (e.type === 'multiplication') {
        const f = fmtItem(e.a, e.b);
        if (e.n === 4) {
            return [
                { p: `Un carré a un côté de ${f} cm. Quelle est son aire ?`, code: 'aire_confusion' },              // (a/b)²
                { p: `Un carré a un périmètre de ${f} cm. Quelle est la longueur de son côté ?`, code: 'probleme_inverse' }, // ÷ 4
                { p: `${n1} mange ${f} d'un gâteau. Quelle part du gâteau reste-t-il ?`, code: 'complement' },      // 1 − a/b
            ];
        }
        return [
            { p: `Un carré a un côté de ${f} cm. Quel est son périmètre ?`, code: 'mauvaise_figure' },              // 4 × a/b ≠ 3 × a/b
            { p: `Un triangle équilatéral a un périmètre de ${f} cm. Quelle est la longueur de son côté ?`, code: 'probleme_inverse' }, // ÷ 3
            { p: `${n1} mange ${f} d'un gâteau. Quelle part du gâteau reste-t-il ?`, code: 'complement' },
        ];
    }
    const f1 = fmtItem(e.a1, e.b1), f2 = fmtItem(e.a2, e.b2);
    const v1 = e.a1 / e.b1, v2 = e.a2 / e.b2;
    const [gF, pF] = v1 >= v2 ? [f1, f2] : [f2, f1];
    const communs = [
        { p: `${n1} mange ${gF} d'un gâteau et ${n2} en mange ${pF}. Quelle part ${n1} a-t-il mangée de plus que ${n2} ?`, code: 'difference' },        // g − p
        { p: `${n1} possède ${f1} d'une tarte et donne ${f2} de SA part à ${n2}. Quelle part de la tarte ${n2} reçoit-il ?`, code: 'fraction_de_fraction' }, // f1 × f2
    ];
    if (e.type === 'somme') {
        return [
            { p: `${n1} mange ${f1} d'un gâteau et ${n2} en mange ${f2}. Quelle part du gâteau reste-t-il ?`, code: 'complement' }, // 1 − f1 − f2
            ...communs,
        ];
    }
    return [
        { p: `${n1} mange ${f1} d'un gâteau et ${n2} en mange ${f2}. Quelle part ont-ils mangée au total ?`, code: 'part_prise' }, // f1 + f2
        ...communs,
    ];
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 1 — associer_calcul_probleme
// ═══════════════════════════════════════════════════════════

/**
 * Direction 1 : QCM calcul → problème
 * Affiche un calcul KaTeX, 4 énoncés en choix.
 */
const genCalculVersProbleme = () => {
    // Les 3 problèmes distracteurs gardent les MÊMES nombres que le calcul
    // affiché mais changent la question (grilles problemesDistracteurs, garde
    // d'exclusivité structurelle vérifiée gabarit par gabarit).
    const correct = buildEntry(randomChoice(['somme', 'reste', 'multiplication']));
    const bonne = correct.enonce;

    const kept = [];
    for (const d of problemesDistracteurs(correct)) {
        if (d.p === bonne || kept.some(k => k.p === d.p)) continue;
        kept.push(d);
    }

    const choix = [bonne, ...kept.map(d => d.p)];
    const texte = `<p>Quel problème peut être résolu par le calcul suivant\u00a0?</p>{{element:0}}`;

    const payload = makeQcm(choix, bonne, texte, opts(L_CALC_PROB));
    // Add expression as element additionnel
    payload.elements_additionnels = {
        version: 1,
        elements: [{
            type: 'expression',
            config: { contenu: correct.exprKatex },
        }],
    };
    payload._meta.generatorKey = 'associer_calcul_probleme';
    payload._meta.params = { distracteurs: Object.fromEntries(kept.map(d => [d.p, d.code])) };
    return payload;
};

/**
 * Direction 2 : QCM problème → calcul
 * Affiche un énoncé textuel, 4 expressions en choix — les distracteurs sont
 * les faux calculs sur LES données du problème (erreurs nommées), avec garde
 * d'unicité des VALEURS (deux choix numériquement égaux : on retire le second).
 */
const genProblemeVersCalcul = () => {
    // Certaines paires rendent deux erreurs numériquement égales (ex. reste
    // avec 1/2 et 1/6 : 1 − 1/2 − 1/6 = 1/2 − 1/6) : la garde retire alors des
    // distracteurs → on REJETTE le tirage et on retire, jusqu'à une grille
    // complète (3 distracteurs survivants).
    let correct = null;
    let kept = [];
    for (let t = 0; t < 40 && !correct; t++) {
        const cand = buildEntry(randomChoice(['somme', 'reste', 'multiplication']));
        const prisVals = [valeurEntry(cand)];
        const k = [];
        for (const d of distracteursPourCalcul(cand)) {
            if (d.item === cand.exprItem || k.some(x => x.item === d.item)) continue;
            if (prisVals.some(v => Math.abs(v - d.v) < 1e-9)) continue;
            prisVals.push(d.v);
            k.push(d);
        }
        if (k.length === 3 || (t >= 30 && k.length >= 2)) {
            correct = cand;
            kept = k;
        }
    }
    if (!correct) {
        correct = buildEntry('multiplication');   // grille toujours complète (v > 0 ⇒ pas de collision)
        kept = distracteursPourCalcul(correct);
    }
    const bonne = correct.exprItem;

    const choix = [bonne, ...kept.map(d => d.item)];
    const texte = `<p>${correct.enonce}</p><p>Quel calcul permet de résoudre ce problème\u00a0?</p>`;

    const payload = makeQcm(choix, bonne, texte, opts(L_PROB_CALC));
    payload._meta.generatorKey = 'associer_calcul_probleme';
    payload._meta.params = { distracteurs: Object.fromEntries(kept.map(d => [d.item, d.code])) };
    return payload;
};

/**
 * Relier : gabarit D3 — 2 sources (2 problèmes de familles différentes),
 * 4 cibles : les 2 calculs corrects + 2 leurres construits sur les données
 * d'UN des deux problèmes (grilles distracteursPourCalcul, avec code).
 */
const genRelier = () => {
    let e1 = null, e2 = null;
    for (let t = 0; t < 30 && !e1; t++) {
        const cand1 = buildEntry(randomChoice(['somme', 'reste', 'multiplication']));
        const autres = ['somme', 'reste', 'multiplication'].filter(x => x !== cand1.type);
        const cand2 = buildEntry(randomChoice(autres));
        if (cand1.exprItem !== cand2.exprItem && cand1.enonce !== cand2.enonce) {
            e1 = cand1;
            e2 = cand2;
        }
    }
    if (!e1) {
        e1 = buildEntry('multiplication');
        e2 = buildEntry('somme');
    }

    // Source raccourcie à ~80 caractères, mais jamais coupée dans une formule
    // KaTeX (les fractions des énoncés sont en `$…$` depuis l'audit fractions
    // en texte, 2026-09-06) : la coupe se fait sur un espace hors formule.
    const raccourcir = (s) => {
        if (s.length <= 80) return s;
        let coupe = -1;
        let dollars = 0;
        for (let i = 0; i < 77; i++) {
            if (s[i] === '$') dollars++;
            if (s[i] === ' ' && dollars % 2 === 0) coupe = i;
        }
        return coupe > 0 ? s.substring(0, coupe) + '...' : s;
    };
    const pairs = [e1, e2].map(e => ({
        source: raccourcir(e.enonce),
        target: e.exprItem,
    }));

    // Un leurre par source : le premier candidat de sa grille qui n'entre en
    // collision ni avec une cible ni avec un autre leurre (texte ET valeur)
    const prisTxt = new Set(pairs.map(p => p.target));
    const prisVals = [valeurEntry(e1), valeurEntry(e2)];
    const leurres = [];
    for (const e of [e1, e2]) {
        for (const d of distracteursPourCalcul(e)) {
            if (prisTxt.has(d.item)) continue;
            if (prisVals.some(v => Math.abs(v - d.v) < 1e-9)) continue;
            prisTxt.add(d.item);
            prisVals.push(d.v);
            leurres.push({ target: d.item, code: d.code });
            break;
        }
    }

    const texte = '<p>Relie chaque problème au calcul qui permet de le résoudre. '
        + 'Attention : certains calculs ne correspondent à aucun problème.</p>';
    const payload = makeRelier(pairs, texte, { ...opts(L_RELIER), cibles_leurres: leurres });
    payload._meta.generatorKey = 'associer_calcul_probleme';
    payload._meta.params = {
        distracteurs: Object.fromEntries(leurres.map(l => [l.target, l.code])),
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// CONTRAT DISTRACTEURS (machine-readable, modèle gen_603)
// ═══════════════════════════════════════════════════════════

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'tout distracteur est construit sur LES DONNÉES du problème posé (jamais des nombres d\'un autre tirage : l\'appariement de surface redevenait possible)',
        'chaque distracteur matérialise une erreur d\'élève NOMMÉE ; le code voyage dans _meta.params.distracteurs (futur feedback D4)',
        'garde d\'unicité des VALEURS : deux choix numériquement égaux → le second est retiré (ex. 2 × a/b = a/b + a/b : ne jamais présenter les deux)',
        'soustractions écrites plus grande fraction en premier (résultat positif, niveau 6e)',
        'SUPPRIMÉ par arbitrage — ne pas réintroduire : a/b + a/b en plus de 2 × a/b (même valeur, l\'élève « doubleur » hésiterait entre deux cases représentant sa propre erreur)',
    ],
    probleme_vers_calcul: {
        carre: { aire_confusion: 'a/b × a/b (il calcule l\'aire)', double_cotes: '2 × a/b (il double : 2 côtés ou réflexe rectangle)', addition_confusion: '4 + a/b (il additionne au lieu de multiplier)' },
        triangle_equilateral: { formule_carre: '4 × a/b (il récite la formule du carré)', double_cotes: '2 × a/b', addition_confusion: '3 + a/b' },
        somme: { complement: '1 − f₁ − f₂ (il répond à « quelle part reste-t-il ? »)', mult_confusion: 'f₁ × f₂', soustraction: 'g − p (plus grande en premier)' },
        reste: { part_prise: 'f₁ + f₂ (il oublie le complément — LA classique)', erreur_signe: '1 − f₁ + f₂', soustraction: 'g − p' },
    },
    calcul_vers_probleme: {
        regle: 'les problèmes distracteurs gardent les MÊMES nombres mais changent la question ; garde d\'exclusivité STRUCTURELLE : aucun problème distracteur ne doit être résoluble par le calcul affiché (piège documenté : « 4 amis mangent chacun a/b » se résout AUSSI par 4 × a/b — interdit)',
        codes: ['aire_confusion', 'probleme_inverse', 'complement', 'mauvaise_figure', 'part_prise', 'difference', 'fraction_de_fraction'],
    },
    relier: 'gabarit D3 : 2 problèmes de familles différentes / 4 cibles (2 calculs corrects + 2 leurres issus des grilles ci-dessus, un par source), makeRelier(..., { cibles_leurres })',
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    associer_calcul_probleme_qcm_unique:  genCalculVersProbleme,
    associer_probleme_calcul_qcm_unique:  genProblemeVersCalcul,
    associer_calcul_probleme_relier:      genRelier,
};

export const CATEGORIES = [
    {
        id: 'associer_calcul_probleme',
        label: 'Associer calcul et problème',
        formatsList: [
            { key: 'associer_calcul_probleme_qcm_unique', label: 'Calcul → Problème' },
            { key: 'associer_probleme_calcul_qcm_unique', label: 'Problème → Calcul' },
            { key: 'associer_calcul_probleme_relier', label: 'Relier' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    gcd, simplify, fmtFrac, fmtKatex, fmtItem,
    FRAC_PAIRS, MULT_ENTRIES,
    buildEntry, valeurEntry, distracteursPourCalcul, problemesDistracteurs, PRENOMS,
};
